/**
 * A dedicated, purpose-built page for previewing and sending a real test
 * copy of the app's two transactional emails (portal invite, password
 * reset) — the Developer nav category's one view.
 *
 * Deliberately not a shortcut into the full Document Templates composer
 * (`/staff/document-templates`): that page is where the firm's actual legal
 * notice/lease content gets authored and published, and reusing it wholesale
 * here would surface a lot of chrome (publish, version history, forwarding-
 * address clause) that has nothing to do with "does the mail pipeline
 * actually work." This page calls the exact same two backend endpoints that
 * page already uses for these two kinds — `POST
 * /staff/document-templates/preview` and `.../send-test-email` — nothing new
 * on the API side, gated by the same `document-templates:write` capability.
 */

import { useState } from 'react';
import { TRANSACTIONAL_EMAIL_TOKEN_GROUPS, type TransactionalEmailTemplateKind } from 'shared';
import { api, ApiError } from '../../../lib/api.js';
import { Field, Select, TextArea, humanizeEnum } from '../../../components/Form.js';
import { Lightbox } from '../../../components/Lightbox.js';
import { RichTextEditor } from '../../../components/RichTextEditor.js';

const TEST_EMAIL_KINDS: readonly TransactionalEmailTemplateKind[] = [
  'PORTAL_INVITE_EMAIL',
  'PASSWORD_RESET_EMAIL',
];
const DEFAULT_KIND: TransactionalEmailTemplateKind = 'PORTAL_INVITE_EMAIL';

interface EmailPreview {
  subject: string;
  html: string;
}

export function TestEmails() {
  const [kind, setKind] = useState<TransactionalEmailTemplateKind>(DEFAULT_KIND);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [bodyIsEmpty, setBodyIsEmpty] = useState(true);

  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState<unknown>(null);
  const [preview, setPreview] = useState<EmailPreview | null>(null);

  const [sendBusy, setSendBusy] = useState(false);
  const [sendError, setSendError] = useState<unknown>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  function buildPayload() {
    return { kind, subject: subject.trim() || null, body, forwardingAddressClause: null };
  }

  function onKindChange(value: string) {
    setKind(value as TransactionalEmailTemplateKind);
    setPreview(null);
    setSentTo(null);
  }

  async function runPreview() {
    if (bodyIsEmpty) return;
    setPreviewError(null);
    setPreviewBusy(true);
    try {
      const result = await api<{ subject: string; html: string; text: string }>(
        '/staff/document-templates/preview',
        { method: 'POST', body: JSON.stringify(buildPayload()) },
      );
      setPreview({ subject: result.subject, html: result.html });
    } catch (caught) {
      setPreviewError(caught);
    } finally {
      setPreviewBusy(false);
    }
  }

  async function sendTest() {
    if (bodyIsEmpty) return;
    setSendError(null);
    setSendBusy(true);
    try {
      const { sentTo: recipient } = await api<{ sentTo: string }>(
        '/staff/document-templates/send-test-email',
        { method: 'POST', body: JSON.stringify(buildPayload()) },
      );
      setSentTo(recipient);
    } catch (caught) {
      setSendError(caught);
    } finally {
      setSendBusy(false);
    }
  }

  return (
    <section className="stack">
      <h1>Test Emails</h1>
      <p className="muted">
        Preview and send a real test copy of {humanizeEnum(kind).toLowerCase()} to your own inbox —
        useful for confirming the mail pipeline actually works in a given environment. This does not
        publish anything; nothing typed here is saved.
      </p>

      <div className="card stack">
        <Field label="Kind" name="kind">
          <Select name="kind" value={kind} onChange={onKindChange} options={TEST_EMAIL_KINDS} />
        </Field>

        <Field
          label="Email subject"
          name="subject"
          hint="Merge fields like {{recipient.name}} apply here too."
        >
          <TextArea name="subject" rows={1} value={subject} onChange={setSubject} />
        </Field>

        <Field
          label="Body"
          name="body"
          hint="Use the toolbar to format text and insert merge fields."
        >
          <RichTextEditor
            value={body}
            onChange={setBody}
            onEmptyChange={setBodyIsEmpty}
            tokenGroups={TRANSACTIONAL_EMAIL_TOKEN_GROUPS}
          />
        </Field>

        {previewError ? (
          <p className="alert alert--error">
            {previewError instanceof ApiError
              ? previewError.message
              : 'Could not generate the preview.'}
          </p>
        ) : null}
        {sendError ? (
          <p className="alert alert--error">
            {sendError instanceof ApiError ? sendError.message : 'Could not send the test email.'}
          </p>
        ) : null}

        <div className="row">
          <button
            type="button"
            className="button button--quiet"
            disabled={previewBusy || bodyIsEmpty}
            onClick={() => void runPreview()}
          >
            {previewBusy ? 'Generating…' : 'Preview'}
          </button>
          <button
            type="button"
            className="button"
            disabled={sendBusy || bodyIsEmpty}
            onClick={() => void sendTest()}
          >
            {sentTo ? `Sent to ${sentTo}` : sendBusy ? 'Sending…' : 'Send test email to myself'}
          </button>
        </div>
      </div>

      {preview ? (
        <Lightbox title="Email preview" onClose={() => setPreview(null)}>
          <p>
            <strong>Subject:</strong> {preview.subject}
          </p>
          <iframe
            title="Email preview"
            srcDoc={preview.html}
            className="document-template-preview-frame"
          />
        </Lightbox>
      ) : null}
    </section>
  );
}
